/**
 * Login - 用户登录页面
 * UI-UX-Pro-Max Design System Applied
 * 左侧品牌展示，右侧登录表单
 */
import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { useSiteConfig } from '../../contexts/SiteConfigContext';
import { 
  Eye, 
  EyeOff, 
  ArrowRight,
  Sparkles,
  BookOpen,
  Baby,
  Palette
} from 'lucide-react';
import Button from '../../components/ui/Button';

// API 基础地址（开发环境通过 Vite proxy，生产环境通过 Nginx）
const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

// 特性配置 - UI-UX-Pro-Max: 使用 SVG 图标代替 emoji
const features = [
  { icon: Sparkles, text: 'AI 智能生成独特故事', color: 'text-amber-300' },
  { icon: Palette, text: '精美插画风格可选', color: 'text-cyan-300' },
  { icon: Baby, text: '适龄内容智能匹配', color: 'text-pink-300' },
];

export default function Login() {
  const navigate = useNavigate();
  const { login, isAuthenticated } = useAuth();
  const { allowRegister } = useSiteConfig();
  const [loginMethod, setLoginMethod] = useState<'password' | 'code'>('password');
  const [formData, setFormData] = useState({
    email: '',
    phone: '',
    password: '',
    code: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [countdown, setCountdown] = useState(0);

  // 如果已登录，跳转到首页
  if (isAuthenticated) {
    navigate('/', { replace: true });
    return null;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // 获取账号（邮箱或手机号）
      const account = formData.email || formData.phone;
      
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          account,
          password: formData.password,
          code: formData.code,
        }),
      });

      const data = await response.json();

      if (data.success) {
        await login(data.data.token);
        navigate('/');
      } else {
        setError(data.error || '登录失败');
      }
    } catch (err) {
      setError('网络错误，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  const sendCode = async () => {
    if (countdown > 0) return;
    
    const target = loginMethod === 'code' ? formData.phone || formData.email : '';
    if (!target) {
      setError('请先输入手机号或邮箱');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/auth/send-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          target,
          type: 'login'
        }),
      });

      const data = await response.json();
      if (data.success) {
        setCountdown(60);
        const timer = setInterval(() => {
          setCountdown(prev => {
            if (prev <= 1) {
              clearInterval(timer);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
        // 开发环境自动填充验证码并提示
        if (data.data?.code) {
          setFormData(prev => ({ ...prev, code: data.data.code }));
          const smsMsg = data.data?.smsDetail === '短信发送成功' ? '验证码短信已发送，请注意查收。\n\n' : '';
          alert(`${smsMsg}【开发环境】验证码为：${data.data.code}\n已自动填入输入框`);
        }
      } else {
        setError(data.error || '发送失败');
      }
    } catch (err) {
      setError('发送验证码失败');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50/30 to-white flex font-body">
      {/* 左侧品牌展示 - UI-UX-Pro-Max Design */}
      <div className="hidden lg:flex lg:w-1/2 xl:w-3/5 bg-gradient-to-br from-indigo-500 via-violet-500 to-purple-600 relative overflow-hidden">
        {/* 装饰背景 */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-20 w-72 h-72 bg-white/10 rounded-full blur-3xl animate-float" />
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-purple-400/20 rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }} />
          <div className="absolute top-1/2 left-1/3 w-64 h-64 bg-indigo-400/20 rounded-full blur-3xl animate-float" style={{ animationDelay: '4s' }} />
        </div>

        <div className="relative z-10 flex flex-col justify-center items-center px-16 xl:px-24 h-full w-full">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-xl w-full"
          >
            {/* Logo */}
            <div className="flex items-center gap-3 mb-10">
              <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center shadow-xl">
                <BookOpen className="w-8 h-8 text-indigo-500" />
              </div>
              <span className="font-display text-4xl font-bold text-white">BadiBook</span>
            </div>

            {/* 主标题 */}
            <h1 className="font-display text-5xl xl:text-6xl font-bold text-white mb-6 leading-snug whitespace-nowrap">
              为孩子创造<span className="text-amber-300">专属绘本故事</span>
            </h1>
            
            <p className="text-xl text-indigo-100 mb-12 max-w-xl leading-relaxed whitespace-nowrap">
              AI 驱动的个性化绘本创作平台，让每个孩子都成为故事的主角
            </p>

            {/* 特性展示 - 卡片式 */}
            <div className="space-y-4 w-full">
              {features.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 + index * 0.1 }}
                  className="flex items-center gap-4 rounded-xl p-4 bg-white/10 backdrop-blur-sm border border-white/20"
                >
                  <div className={`w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center ${item.color}`}>
                    <item.icon className="w-6 h-6" />
                  </div>
                  <span className="text-lg text-white/90 font-medium">{item.text}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>

      {/* 右侧登录表单 */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          {/* 移动端 Logo */}
          <div className="lg:hidden flex items-center gap-3 mb-8 justify-center">
            <div className="w-14 h-14 bg-gradient-to-br from-indigo-500 to-violet-500 rounded-2xl flex items-center justify-center shadow-lg">
              <BookOpen className="w-7 h-7 text-white" />
            </div>
            <span className="font-display text-3xl font-bold text-indigo-900">BadiBook</span>
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-sm border border-slate-200">
            <h2 className="font-display text-3xl font-bold text-indigo-900 mb-2">欢迎回来</h2>
            <p className="text-indigo-900/50 mb-6">登录您的 BadiBook 账号</p>

            {/* 登录方式切换 */}
            <div className="flex gap-2 p-1.5 bg-indigo-50 rounded-xl mb-6">
              <button
                type="button"
                onClick={() => setLoginMethod('password')}
                className={`flex-1 py-2.5 px-4 rounded-xl text-sm font-bold transition-all ${
                  loginMethod === 'password'
                    ? 'bg-white shadow-sm text-indigo-600'
                    : 'text-slate-500'
                }`}
              >
                密码登录
              </button>
              <button
                type="button"
                onClick={() => setLoginMethod('code')}
                className={`flex-1 py-2.5 px-4 rounded-xl text-sm font-bold transition-all ${
                  loginMethod === 'code'
                    ? 'bg-white shadow-sm text-indigo-600'
                    : 'text-slate-500'
                }`}
              >
                验证码登录
              </button>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-red-500" />
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* 邮箱/手机号输入 */}
              <div>
                <label className="block text-sm font-medium text-indigo-900 mb-2">
                  {loginMethod === 'code' ? '手机号或邮箱' : '邮箱/手机号'}
                </label>
                <input
                  type="text"
                  value={formData.email || formData.phone}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value.includes('@')) {
                      setFormData(prev => ({ ...prev, email: value, phone: '' }));
                    } else {
                      setFormData(prev => ({ ...prev, phone: value, email: '' }));
                    }
                  }}
                  placeholder="请输入邮箱或手机号"
                  className="w-full px-4 py-3.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all font-body"
                  required
                />
              </div>

              {/* 密码或验证码输入 */}
              {loginMethod === 'password' ? (
                <div>
                  <label className="block text-sm font-medium text-indigo-900 mb-2">
                    密码
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                      placeholder="请输入密码"
                      className="w-full px-4 pr-12 py-3.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all font-body"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-indigo-400 hover:text-indigo-600 transition-colors cursor-pointer"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  <div className="mt-2 text-right">
                    <Link to="/forgot-password" className="text-sm text-indigo-500 hover:text-indigo-600 font-medium">
                      忘记密码？
                    </Link>
                  </div>
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-indigo-900 mb-2">
                    验证码
                  </label>
                  <div className="flex gap-3">
                    <input
                      type="text"
                      value={formData.code}
                      onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value }))}
                      placeholder="请输入验证码"
                      className="flex-1 px-4 py-3.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all font-body"
                      required
                    />
                    <button
                      type="button"
                      onClick={sendCode}
                      disabled={countdown > 0}
                      className={`px-5 py-3.5 rounded-xl font-bold transition-all whitespace-nowrap ${
                        countdown > 0
                          ? 'bg-indigo-100 text-indigo-400 cursor-not-allowed'
                          : 'bg-indigo-100 text-indigo-600 hover:bg-indigo-200'
                      }`}
                    >
                      {countdown > 0 ? `${countdown}s` : '获取验证码'}
                    </button>
                  </div>
                </div>
              )}

              <Button
                type="submit"
                size="lg"
                className="w-full bg-indigo-600 text-white rounded-xl h-12 font-semibold hover:bg-indigo-700"
                isLoading={loading}
              >
                {loading ? '登录中...' : '登录'}
                {!loading && <ArrowRight className="w-5 h-5 ml-2" />}
              </Button>
            </form>

            {/* 注册入口 */}
            {allowRegister && (
              <div className="mt-6 text-center">
                <p className="text-indigo-900/60">
                  还没有账号？{' '}
                  <Link to="/register" className="text-indigo-500 hover:text-indigo-600 font-bold">
                    立即注册
                  </Link>
                </p>
              </div>
            )}
          </div>

          {/* 返回首页 */}
          <div className="mt-6 text-center">
            <Link to="/" className="text-sm text-indigo-400 hover:text-indigo-500 font-medium">
              ← 返回首页
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
